import {
  AbstractControl, AsyncValidator, NG_ASYNC_VALIDATORS, ValidationErrors,
} from '@angular/forms';
import {
  Directive,
  forwardRef,
  Inject,
  Input,
} from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Directive for performing asynchronous validation of form fields via AJAX calls.
 * This directive integrates with Angular's reactive forms system to provide real-time
 * validation feedback by making HTTP requests to validate field values.
 *
 * The directive should be applied to input elements that need AJAX-based validation,
 * and requires specific attributes to configure the validation behavior.
 *
 * Validation occurs only when the control has been modified and contains a value
 * different from the existing value. If the field is pristine or matches the
 * existing value, no validation is performed.
 *
 * The directive provides asynchronous validation by implementing the AsyncValidator interface,
 * returning Observable<ValidationErrors | null> from the validate method.
 *
 * Validation results are processed to either return null (valid) or a validation error object
 * with the validation key and optional error code. Network errors during validation
 * result in a generic validation error without a specific code.
 */
@Directive({
  selector: 'aero-input[bulbValidationField][bulbAjaxValidation]',
  standalone: false,
  providers: [{
    provide: NG_ASYNC_VALIDATORS,
    useExisting: forwardRef(() => AccountAjaxValidationDirective),
    multi: true,
  }],
})
export class AccountAjaxValidationDirective implements AsyncValidator {
  @Input() bulbAjaxValidation: string;

  @Input() accountValidationExistingValue: string;

  @Input() bulbAjaxValidationOptions: any;

  /**
   * Creates a new instance of the component with the specified AJAX service dependency.
   * @param ajaxService The injected BulbAjaxService instance used for making AJAX requests
   * @return A new instance of the component initialized with the provided AJAX service
   */
  constructor(@Inject('BulbAjaxService') private ajaxService: any) {}

  /**
   * Validates the control value against a remote validation service.
   *
   * @param control - The abstract control to validate
   * @return An observable that emits validation errors or null if valid
   */
  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    if (!control.dirty || !control.value || control.value === this.accountValidationExistingValue) {
      return of(null);
    }

    return from(
      this.ajaxService.validateField(this.bulbAjaxValidation, control.value, this.bulbAjaxValidationOptions),
    ).pipe(
      map((result: any) => {
        if (result && result.valid) {
          return null;
        }
        const key = this.bulbAjaxValidation;
        return { [key]: true, code: result?.code };
      }),
      catchError(() => {
        const key = this.bulbAjaxValidation;
        return of({ [key]: true });
      }),
    );
  }
}
